import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Edit,
  Gift,
  PlayCircle,
  Plus,
  Search,
  Trash2,
  Trophy,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DrawType,
  rewardsService,
  RewardStatus,
} from '@/services/rewards.service';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ExecuteDrawModal } from './components/execute-draw-modal';
import { ParticipationsTable } from './components/participations-table';
import { RewardFormModal } from './components/reward-form-modal';
import { WinnersTable } from './components/winners-table';

export function RewardsListPage() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingReward, setEditingReward] = useState<any>(null); // eslint-disable-line @typescript-eslint/no-explicit-any
  const [executingDraw, setExecutingDraw] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [viewingParticipations, setViewingParticipations] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [viewingWinners, setViewingWinners] = useState<{
    id: string;
    title: string;
  } | null>(null);

  // Fetch rewards
  const { data, isLoading } = useQuery({
    queryKey: ['rewards', searchTerm],
    queryFn: () =>
      rewardsService.getRewards({
        filter: searchTerm,
        maxResultCount: 100,
        skipCount: 0,
      }),
  });

  // Delete reward
  const deleteMutation = useMutation({
    mutationFn: (id: string) => rewardsService.deleteReward(id),
    onSuccess: () => {
      toast.success('Reward deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['rewards'] });
    },
    onError: () => {
      toast.error('Failed to delete reward');
    },
  });

  const handleDelete = (id: string, title: string) => {
    if (confirm(`Are you sure you want to delete "${title}"?`)) {
      deleteMutation.mutate(id);
    }
  };

  const getStatusBadge = (status: RewardStatus) => {
    switch (status) {
      case RewardStatus.Draft:
        return <Badge variant="secondary">Draft</Badge>;
      case RewardStatus.Active:
        return <Badge variant="success">Active</Badge>;
      case RewardStatus.Closed:
        return <Badge variant="warning">Closed</Badge>;
      case RewardStatus.Drawn:
        return <Badge variant="info">Drawn</Badge>;
      case RewardStatus.Cancelled:
        return <Badge variant="destructive">Cancelled</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getDrawTypeBadge = (drawType: DrawType) => {
    return drawType === DrawType.Automatic ? (
      <Badge variant="outline">Auto</Badge>
    ) : (
      <Badge variant="outline">Manual</Badge>
    );
  };

  const rewards = data?.items || [];
  const activeRewards = rewards.filter(
    (r) => r.status === RewardStatus.Active,
  ).length;
  const totalParticipants = rewards.reduce(
    (sum, r) => sum + r.currentParticipants,
    0,
  );
  const drawnRewards = rewards.filter(
    (r) => r.status === RewardStatus.Drawn,
  ).length;

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Prize Draws</h1>
          <p className="text-muted-foreground mt-1">
            Manage reward draws and giveaways
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Reward
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Total Rewards</p>
              <Gift className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-3xl font-bold">{rewards.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Active Draws</p>
              <PlayCircle className="h-4 w-4 text-success" />
            </div>
            <p className="text-3xl font-bold text-success">{activeRewards}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">
                Total Participants
              </p>
              <Users className="h-4 w-4 text-primary" />
            </div>
            <p className="text-3xl font-bold text-primary">
              {totalParticipants.toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Drawn</p>
              <Trophy className="h-4 w-4 text-yellow-500" />
            </div>
            <p className="text-3xl font-bold text-yellow-500">{drawnRewards}</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search rewards..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Rewards Table */}
      <Card>
        <CardHeader>
          <CardTitle>Reward Draws</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse h-16 bg-muted rounded" />
              ))}
            </div>
          ) : rewards.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead className="text-right">Tokens Required</TableHead>
                  <TableHead>Draw Type</TableHead>
                  <TableHead className="text-right">Participants</TableHead>
                  <TableHead className="text-right">Winners</TableHead>
                  <TableHead>Draw Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rewards.map((reward) => (
                  <TableRow key={reward.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{reward.title}</p>
                        {reward.shortDescription && (
                          <p className="text-sm text-muted-foreground truncate max-w-xs">
                            {reward.shortDescription}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-semibold text-primary">
                      {reward.tokensRequired.toLocaleString()}
                    </TableCell>
                    <TableCell>{getDrawTypeBadge(reward.drawType)}</TableCell>
                    <TableCell className="text-right">
                      {reward.currentParticipants}
                      {reward.maxParticipants && ` / ${reward.maxParticipants}`}
                    </TableCell>
                    <TableCell className="text-right">
                      {reward.winnerCount}
                    </TableCell>
                    <TableCell>
                      {reward.drawExecutedAt
                        ? format(
                            new Date(reward.drawExecutedAt),
                            'MMM dd, yyyy',
                          )
                        : format(new Date(reward.endDate), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell>{getStatusBadge(reward.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* Participants Button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setViewingParticipations({
                              id: reward.id,
                              title: reward.title,
                            })
                          }
                          title="View Participants"
                        >
                          <Users className="w-4 h-4" />
                        </Button>

                        {/* Winners Button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setViewingWinners({
                              id: reward.id,
                              title: reward.title,
                            })
                          }
                          title="View Winners"
                        >
                          <Trophy className="w-4 h-4" />
                        </Button>

                        {/* Execute Draw Button - Show for Active/Closed with participants */}
                        {reward.status !== RewardStatus.Drawn &&
                          reward.currentParticipants > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                console.log(
                                  'Execute draw clicked for:',
                                  reward,
                                );
                                setExecutingDraw({
                                  id: reward.id,
                                  title: reward.title,
                                });
                              }}
                              title="Execute Draw"
                              className="text-success hover:text-success hover:bg-success/10"
                            >
                              <PlayCircle className="w-4 h-4 fill-success" />
                            </Button>
                          )}

                        {/* Edit Button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingReward(reward)}
                          title="Edit Reward"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>

                        {/* Delete Button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(reward.id, reward.title)}
                          title="Delete Reward"
                        >
                          <Trash2 className="w-4 h-4 text-danger" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Gift className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No rewards found</p>
              <p className="text-sm mt-2">
                Create your first reward draw to get started
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingReward) && (
        <RewardFormModal
          open={showCreateModal || !!editingReward}
          onClose={() => {
            setShowCreateModal(false);
            setEditingReward(null);
          }}
          reward={editingReward}
        />
      )}

      {/* Execute Draw Modal */}
      {executingDraw && (
        <ExecuteDrawModal
          open={!!executingDraw}
          onClose={() => setExecutingDraw(null)}
          rewardId={executingDraw.id}
          rewardTitle={executingDraw.title}
        />
      )}

      {/* Participations Dialog */}
      {viewingParticipations && (
        <Dialog
          open={!!viewingParticipations}
          onOpenChange={(open) => !open && setViewingParticipations(null)}
        >
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogTitle className="sr-only">
              Participants for {viewingParticipations.title}
            </DialogTitle>
            <ParticipationsTable
              rewardId={viewingParticipations.id}
              rewardTitle={viewingParticipations.title}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Winners Dialog */}
      {viewingWinners && (
        <Dialog
          open={!!viewingWinners}
          onOpenChange={(open) => !open && setViewingWinners(null)}
        >
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogTitle className="sr-only">
              Winners for {viewingWinners.title}
            </DialogTitle>
            <WinnersTable
              rewardId={viewingWinners.id}
              rewardTitle={viewingWinners.title}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default RewardsListPage;
